export type LandingCopy = {
  eyebrow: string;
  headline: string;
  subheadline: string;
  promise: string;
  audience: string;
  ctaLabel: string;
  secondaryCtaLabel: string;
  painPoints: string[];
  outcomes: string[];
  proofPoints: string[];
  demoScenario: string;
  trafficAngle: string;
  faqs: Array<{ question: string; answer: string }>;
};

export type LandingVariant = Partial<LandingCopy> & {
  id: "control" | "pain" | "roi";
  name: string;
  rationale: string;
};

export type LandingProof = {
  title: string;
  metric: string;
  caption: string;
  sampleInput: string;
  sampleOutput: string;
};

export const productLandingCopy: Record<string, LandingCopy> = {
  "messaging:tiendita-bot-os": {
    eyebrow: "Para negocios que ya venden por WhatsApp",
    headline: "Convierte tu WhatsApp en una tienda con catálogo, pedidos, inventario y pagos.",
    subheadline: "TienditaBot OS ayuda a microcomercios, restaurantes y vendedores informales a ordenar ventas sin obligarlos a cambiar de canal.",
    promise: "Menos pedidos perdidos, más control de stock y una experiencia de compra más seria desde el mismo chat.",
    audience: "Vendedores por WhatsApp, restaurantes pequeños, tiendas de barrio, creators con productos y comercios sin ecommerce formal.",
    ctaLabel: "Crear mi tienda por WhatsApp",
    secondaryCtaLabel: "Ver herramientas incluidas",
    painPoints: [
      "Los pedidos llegan mezclados entre chats personales y clientes.",
      "El inventario se actualiza tarde y se venden productos agotados.",
      "Los clientes preguntan precios, tallas y disponibilidad una y otra vez.",
      "Los cobros, recibos y entregas quedan sin seguimiento claro.",
    ],
    outcomes: [
      "Catálogo simple con productos, precios y stock.",
      "Pedidos estructurados con total, cliente, entrega y estado.",
      "Recibos, links de pago y tarjetas de fidelidad listas para usar.",
      "Broadcasts y menú del día con opt-in y aprobación humana.",
    ],
    proofPoints: ["Workspace por negocio", "Auditoría de pedidos", "Pagos MercadoPago", "Opt-in para broadcasts"],
    demoScenario: "Agrega un producto, registra una venta, genera recibo y prepara el mensaje de confirmación para el cliente.",
    trafficAngle: "Landing para pauta a microcomercios: vender más por WhatsApp sin montar una tienda compleja.",
    faqs: [
      { question: "Necesito una página web?", answer: "No. El producto empieza desde WhatsApp/Telegram y un workspace web para administrar catálogo, pedidos y clientes." },
      { question: "Puede enviar mensajes masivos?", answer: "Solo con opt-in, límites y aprobación. No está diseñado para spam ni automatización prohibida." },
      { question: "Puedo cobrar?", answer: "Sí. El backend ya tiene proveedor de pagos y puede generar flujos de checkout o links de pago según configuración." },
    ],
  },
  "messaging:arriendo-bot-propertyops": {
    eyebrow: "Para propietarios e inmobiliarias pequeñas",
    headline: "Administra arriendos, mora, recibos y mantenimiento sin perderte en chats.",
    subheadline: "ArriendoBot centraliza propiedades, inquilinos, pagos, recordatorios y reportes de mantenimiento en un workspace operable.",
    promise: "Menos mora olvidada, más trazabilidad y comunicaciones formales mejor preparadas.",
    audience: "Propietarios con 1-20 inmuebles, administradores independientes e inmobiliarias pequeñas.",
    ctaLabel: "Organizar mis arriendos",
    secondaryCtaLabel: "Ver flujo de propiedad",
    painPoints: [
      "Cada inquilino reporta problemas por un chat distinto.",
      "Los pagos, recibos y mora se controlan manualmente.",
      "Los mantenimientos no tienen historial ni responsable claro.",
      "Las comunicaciones legales se redactan tarde o sin estructura.",
    ],
    outcomes: [
      "Registro de inmuebles, inquilinos, pagos y proveedores.",
      "Recordatorios de cobro, mora y vencimientos.",
      "Reportes de mantenimiento con estado, costo y responsable.",
      "Borradores formales con disclaimer y revisión humana.",
    ],
    proofPoints: ["Workspace por propiedad", "Historial por inquilino", "Recordatorios", "Aprobación humana"],
    demoScenario: "Registra un inquilino, crea un cobro pendiente y genera un recordatorio de mora con tono profesional.",
    trafficAngle: "Pauta a propietarios: deja de administrar arriendos con memoria y chats sueltos.",
    faqs: [
      { question: "Reemplaza a un abogado?", answer: "No. Genera orientación y borradores. Las decisiones legales requieren revisión profesional." },
      { question: "Sirve para una sola propiedad?", answer: "Sí. También escala a varios inmuebles y equipos pequeños." },
      { question: "Puede recordar cobros?", answer: "Sí. El scheduler puede registrar y disparar recordatorios según configuración del workspace." },
    ],
  },
  "messaging:legal-latam-assistant": {
    eyebrow: "Borradores legales para LATAM",
    headline: "Genera derechos de petición, reclamos y cartas formales con estructura clara.",
    subheadline: "Legal LATAM Assistant convierte hechos desordenados en borradores documentales, checklists y próximos pasos revisables.",
    promise: "Más claridad antes de reclamar, menos documentos improvisados y mejor organización de evidencia.",
    audience: "Personas naturales, independientes y microempresas que necesitan preparar un reclamo, PQRS, multa o checklist migratorio.",
    ctaLabel: "Preparar mi documento",
    secondaryCtaLabel: "Ver disclaimers",
    painPoints: [
      "La persona no sabe qué escribir ni qué anexar.",
      "Se pierden plazos por no entender el proceso.",
      "El reclamo queda emocional, incompleto o sin estructura.",
      "La evidencia no se ordena antes de enviar.",
    ],
    outcomes: [
      "Borrador formal con hechos, solicitudes y anexos.",
      "Checklist de evidencia y plazos.",
      "Lenguaje claro para derechos de petición, PQRS o reclamos.",
      "Advertencias de revisión humana antes de enviar.",
    ],
    proofPoints: ["Disclaimers fuertes", "Trazabilidad", "Aprobación humana", "PDF/documento listo"],
    demoScenario: "Describe un cobro no autorizado o una EPS que no responde y genera un borrador revisable.",
    trafficAngle: "Pauta a usuarios con problemas concretos: convierte tu caso en un documento claro para revisar y enviar.",
    faqs: [
      { question: "Es asesoría legal definitiva?", answer: "No. Es orientación informativa y generación de borradores. Debe revisarlo un profesional cuando el caso sea sensible." },
      { question: "Sirve para Colombia?", answer: "Está pensado para LATAM y permite indicar país/jurisdicción. Los fundamentos deben verificarse antes de envío." },
      { question: "Puedo pagar por documento?", answer: "Sí. El modelo soporta pay-per-doc y plan mensual." },
    ],
  },
  "messaging:freelancer-consultor-bot": {
    eyebrow: "Operaciones para freelancers",
    headline: "Cotiza, factura, da seguimiento y reporta avances desde un solo asistente.",
    subheadline: "Freelancer/Consultor Bot convierte notas, chats y tareas sueltas en CRM, propuestas, recibos y follow-ups.",
    promise: "Menos oportunidades perdidas y más disciplina comercial sin montar un CRM pesado.",
    audience: "Freelancers, consultores, agencias pequeñas y profesionales independientes.",
    ctaLabel: "Ordenar mi operación",
    secondaryCtaLabel: "Ver módulos",
    painPoints: [
      "Los leads se pierden por falta de seguimiento.",
      "Las cotizaciones toman demasiado tiempo.",
      "Las horas y pagos pendientes quedan dispersos.",
      "Los reportes a clientes se escriben desde cero.",
    ],
    outcomes: [
      "CRM simple por cliente y proyecto.",
      "Cotizaciones, propuestas y facturas generadas desde una descripción.",
      "Time tracking y reportes de avance.",
      "Follow-ups sugeridos con aprobación humana.",
    ],
    proofPoints: ["Workspace profesional", "PDF-ready", "Historial de clientes", "Pagos y usage"],
    demoScenario: "Registra un lead, genera una propuesta y crea un follow-up profesional para mañana.",
    trafficAngle: "Pauta a independientes: deja de manejar clientes con notas sueltas.",
    faqs: [
      { question: "Sustituye un CRM completo?", answer: "No pretende competir con CRMs enterprise. Es una capa ligera para freelancers que venden y ejecutan solos." },
      { question: "Genera contratos?", answer: "Puede generar borradores, pero los contratos deben revisarse profesionalmente." },
      { question: "Puede facturar?", answer: "Genera recibos/facturas simples y datos estructurados; la validez fiscal depende del país y configuración." },
    ],
  },
  "messaging:agendabot-personal-os": {
    eyebrow: "Asistente personal por voz y texto",
    headline: "Convierte notas de voz en tareas, eventos, recordatorios y digest diario.",
    subheadline: "AgendaBot entiende intención, fecha, hora, recurrencia y prioridad para ayudarte a llevar agenda y pendientes.",
    promise: "Menos cosas olvidadas, más claridad diaria y recordatorios accionables.",
    audience: "Profesionales ocupados, founders, consultores, familias y usuarios que viven en WhatsApp/Telegram.",
    ctaLabel: "Crear mi agenda inteligente",
    secondaryCtaLabel: "Ver cómo funciona",
    painPoints: [
      "Las tareas se quedan en audios, notas y chats.",
      "Los recordatorios se crean tarde o nunca.",
      "La agenda personal y los pendientes no conversan entre sí.",
      "Las recurrencias y cambios se olvidan.",
    ],
    outcomes: [
      "Eventos y tareas desde texto o voz.",
      "Recordatorios únicos o recurrentes.",
      "Digest diario con pendientes vencidos.",
      "Confirmación antes de invitar o notificar a terceros.",
    ],
    proofPoints: ["Scheduler", "Workspaces personales", "Voice-to-task", "Aprobación para terceros"],
    demoScenario: "Escribe 'reunión con Juan el martes a las 3 y recuérdame preparar la propuesta el lunes'.",
    trafficAngle: "Pauta B2C: tu WhatsApp como asistente de agenda y proyectos.",
    faqs: [
      { question: "Se conecta a Google Calendar?", answer: "El backend está preparado para calendario propio y conectores; Google Calendar requiere credenciales/configuración adicional." },
      { question: "Puede mandar invitaciones?", answer: "Sí, pero debe pedir confirmación antes de notificar a terceros." },
      { question: "Funciona con voz?", answer: "Sí cuando el canal/proveedor de transcripción está configurado." },
    ],
  },
  "messaging:whatsapp-business-automation-hub": {
    eyebrow: "Automatización conversacional con aprobación",
    headline: "Responde audios, clientes y plantillas de WhatsApp sin perder control humano.",
    subheadline: "El hub resume mensajes, propone respuestas A/B/C, genera plantillas y prepara workflows con APIs oficiales.",
    promise: "Menos tiempo respondiendo, más consistencia y menos riesgo de automatización peligrosa.",
    audience: "Negocios con alto volumen de WhatsApp, soporte, ventas y equipos que necesitan aprobación antes de enviar.",
    ctaLabel: "Automatizar mi WhatsApp",
    secondaryCtaLabel: "Ver guardrails",
    painPoints: [
      "Los audios largos consumen tiempo del equipo.",
      "Cada vendedor responde con tono distinto.",
      "Las plantillas no están preparadas para aprobación.",
      "La automatización sin control puede generar riesgo reputacional.",
    ],
    outcomes: [
      "Transcripción y resumen de mensajes.",
      "Tres respuestas potenciales listas para aprobar.",
      "Plantillas WhatsApp/SMS con opt-out y placeholders.",
      "Workflows n8n y agenda conversacional con revisión humana.",
    ],
    proofPoints: ["Threads por contacto", "Human approval", "APIs oficiales", "Audit log"],
    demoScenario: "Pega un mensaje largo de cliente y genera resumen más tres respuestas en tono ventas/soporte.",
    trafficAngle: "Pauta a negocios: responde mejor por WhatsApp sin entregar el control total a un bot.",
    faqs: [
      { question: "Envía solo automáticamente?", answer: "No por defecto. Las acciones sensibles requieren aprobación humana." },
      { question: "Usa WhatsApp personal?", answer: "Debe operar con WhatsApp Business/Cloud API o Telegram Bot configurado." },
      { question: "Puede usar audios?", answer: "Sí, con proveedor de transcripción conectado." },
    ],
  },
  "messaging:mobility-auto-assistant": {
    eyebrow: "Asistente para conductores",
    headline: "Controla multas, mantenimiento, SOAT, rutas y gastos de tu vehículo.",
    subheadline: "Mobility & Auto Assistant ayuda a conductores a recordar vencimientos, preparar respuestas y planear viajes sin depender de memoria.",
    promise: "Menos multas olvidadas, menos mantenimiento tarde y más claridad antes de pagar o apelar.",
    audience: "Conductores particulares, familias con vehículo, mensajeros, freelancers y personas que necesitan controlar documentos del carro.",
    ctaLabel: "Organizar mi vehículo",
    secondaryCtaLabel: "Ver herramientas",
    painPoints: [
      "La tecnomecánica, SOAT o cambio de aceite se recuerdan tarde.",
      "Las multas se pagan sin entender descuentos, plazos o evidencia.",
      "Los gastos del vehículo no se registran.",
      "Planear rutas y viajes toma más tiempo del necesario.",
    ],
    outcomes: [
      "Recordatorios de documentos, mantenimiento y vencimientos.",
      "Orientación para multas con disclaimer y checklist.",
      "Plan de viaje con paradas, costos y pendientes.",
      "Historial por vehículo y próximos pasos.",
    ],
    proofPoints: ["Scheduler", "Workspace personal", "Disclaimer legal", "Checklist de mantenimiento"],
    demoScenario: "Registra tu vehículo, agrega vencimiento de SOAT y genera un recordatorio con próximos mantenimientos.",
    trafficAngle: "Pauta a conductores: deja de manejar vencimientos del carro con memoria.",
    faqs: [
      { question: "Me dice si debo apelar una multa?", answer: "Da orientación y checklist, no asesoría legal definitiva. Los casos sensibles deben revisarse." },
      { question: "Encuentra gasolina/parqueaderos en tiempo real?", answer: "Eso depende de providers o datos conectados. El backend está preparado para integrarlos." },
      { question: "Puede recordarme SOAT y tecnomecánica?", answer: "Sí. Scheduler y workspace permiten registrar vencimientos y recordatorios." },
    ],
  },
  "web:media-commerce-studio": {
    eyebrow: "Imagen comercial para sellers",
    headline: "Prepara fotos, thumbnails y assets visuales para vender mejor.",
    subheadline: "Media Commerce Studio agrupa background remover, mejora de producto, upscaling y thumbnails con control de costo.",
    promise: "Mejores imágenes de producto sin pasar por un flujo manual pesado.",
    audience: "Sellers, creadores, restaurantes, ecommerce y equipos de marketing visual.",
    ctaLabel: "Mejorar una imagen",
    secondaryCtaLabel: "Ver módulos visuales",
    painPoints: [
      "Las fotos de producto no se ven listas para marketplace.",
      "Quitar fondos y crear thumbnails toma tiempo.",
      "Los outputs premium dependen de providers caros si no hay cost cap.",
      "Las piezas visuales no quedan trazadas por run/pago.",
    ],
    outcomes: [
      "Background remover y PNG transparente cuando el provider está configurado.",
      "Presets de mejora de producto y thumbnails.",
      "Upscale con límite de costo y control de tamaño.",
      "Artifacts descargables y auditoría de ejecución.",
    ],
    proofPoints: ["Cost cap", "Artifacts", "Workers media", "Checkout por run"],
    demoScenario: "Sube o describe una foto de producto y genera especificación/preset comercial o output real si el provider está conectado.",
    trafficAngle: "Pauta a sellers: convierte fotos normales en assets de venta.",
    faqs: [
      { question: "Ya produce PNG real?", answer: "Sí si está configurado el provider/rembg/GPU correspondiente. Sin provider, opera como spec/demo." },
      { question: "Puede ser caro?", answer: "Por eso el backend modela cost cap, límites de tamaño y pago antes de ejecución premium." },
      { question: "Sirve para ecommerce?", answer: "Sí, especialmente para fotos de catálogo, thumbnails y assets visuales repetibles." },
    ],
  },
  "web:audio-video-lab": {
    eyebrow: "Contenido largo a formatos utilizables",
    headline: "Transcribe, resume, convierte y genera audio/video con workers controlados.",
    subheadline: "Audio & Video Lab empaqueta transcripción, podcast summaries, TTS, audiobooks, noise removal y GIFs bajo colas/cost caps.",
    promise: "Convierte audios y videos largos en texto, capítulos, clips y artifacts listos para entregar.",
    audience: "Podcasters, creadores, educadores, agencias y equipos que reciclan contenido largo.",
    ctaLabel: "Procesar audio o video",
    secondaryCtaLabel: "Ver pipeline",
    painPoints: [
      "Transcribir y resumir contenido largo consume horas.",
      "Los audios necesitan normalización antes de publicar.",
      "TTS/audiobooks pueden disparar costo si no hay límites.",
      "Los archivos finales necesitan links descargables reales.",
    ],
    outcomes: [
      "Transcripción y resumen con capítulos.",
      "TTS y audiobook con control de caracteres/costo.",
      "Noise remover y Video to GIF cuando FFmpeg/provider está disponible.",
      "Artifacts descargables en vez de blobs improvisados.",
    ],
    proofPoints: ["FFmpeg", "Cost cap", "Artifacts", "Workers asíncronos"],
    demoScenario: "Pega un transcript o describe un audio y genera resumen, capítulos y próximos clips.",
    trafficAngle: "Pauta a creators: convierte contenido largo en piezas publicables.",
    faqs: [
      { question: "Transcribe audio directo?", answer: "Sí cuando el proveedor STT está configurado. Si no, acepta transcript input." },
      { question: "Genera MP3 real?", answer: "Depende del provider TTS configurado y los límites de costo." },
      { question: "Puede tardar?", answer: "Sí. Audio/video son trabajos pesados y deben correr con cola o timeout controlado." },
    ],
  },
  "web:automation-integration-lab": {
    eyebrow: "Integraciones sin improvisación",
    headline: "Prueba webhooks, genera workflows y conecta automatizaciones con control.",
    subheadline: "Automation & Integration Lab ayuda a equipos técnicos y ops a crear endpoints, logs, replay y specs n8n sin saltarse seguridad.",
    promise: "Menos automatizaciones frágiles y más trazabilidad antes de conectar producción.",
    audience: "Startups, equipos técnicos, operaciones, agencias y builders de workflows.",
    ctaLabel: "Crear integración",
    secondaryCtaLabel: "Ver controles",
    painPoints: [
      "Los webhooks fallan y nadie tiene logs útiles.",
      "Los workflows se copian sin firma ni auditoría.",
      "Las automatizaciones sensibles se conectan sin aprobación.",
      "No hay separación clara entre spec y despliegue real.",
    ],
    outcomes: [
      "Webhook tester/logger con replay y trazabilidad.",
      "Generación de workflow/spec n8n.",
      "Firmas, redacted logs y controles de seguridad.",
      "Human review para despliegues o canales sensibles.",
    ],
    proofPoints: ["Webhook logs", "Idempotencia", "Redaction", "Human approval"],
    demoScenario: "Define un webhook de checkout y genera endpoint de prueba, payload esperado y workflow n8n.",
    trafficAngle: "Pauta técnica: prueba integraciones antes de romper producción.",
    faqs: [
      { question: "Despliega directo en n8n?", answer: "Solo si N8N_API_URL/API_KEY están configuradas; si no, genera spec/workflow revisable." },
      { question: "Guarda payloads sensibles?", answer: "Debe aplicar redaction y evitar secretos en logs." },
      { question: "Sirve para QA?", answer: "Sí. Webhook logging y replay son especialmente útiles para pruebas end-to-end." },
    ],
  },
  "web:aemed-research-lab": {
    eyebrow: "Investigación clínica, no diagnóstico",
    headline: "Construye protocolos clínicos reproducibles con STROBE, TRIPOD, STARD y TTE.",
    subheadline: "AEMed Research Lab ayuda a investigadores a convertir ideas y datos retrospectivos en planes metodológicos rigurosos.",
    promise: "Más rigor antes de escribir el protocolo, menos supuestos ocultos y mejor trazabilidad metodológica.",
    audience: "Investigadores clínicos, epidemiólogos, médicos académicos, data scientists de salud y comités de investigación.",
    ctaLabel: "Construir protocolo",
    secondaryCtaLabel: "Ver controles éticos",
    painPoints: [
      "Las preguntas clínicas empiezan sin PICO ni guía de reporte clara.",
      "Los sesgos, faltantes y leakage se detectan tarde.",
      "La reproducibilidad queda como una nota secundaria.",
      "Es fácil confundir investigación con recomendación clínica.",
    ],
    outcomes: [
      "Protocolo con diseño, hipótesis, PICO y guía aplicable.",
      "Tabla de supuestos, sesgos, confusores y plan estadístico.",
      "JSON reproducible con metodología y métricas.",
      "Disclaimers estrictos: no diagnostica ni prescribe.",
    ],
    proofPoints: ["MedGemma/Ollama-ready", "Cola larga", "Trazabilidad", "Human review"],
    demoScenario: "Describe una cohorte retrospectiva y genera protocolo STROBE/RECORD con supuestos, análisis y JSON.",
    trafficAngle: "Pauta académica: convierte datos clínicos en un protocolo publicable y revisable.",
    faqs: [
      { question: "Da segunda opinión médica?", answer: "No. Este producto es de investigación/metodología, no diagnóstico ni tratamiento." },
      { question: "Usa MedGemma?", answer: "Está diseñado para integrarse con MedGemma/Ollama y fallback LLM con colas largas." },
      { question: "Puede usar evidencia nueva?", answer: "El siguiente nivel es RAG médico versionado; sin RAG, el usuario debe aportar fuentes/evidencia." },
    ],
  },
  "web:document-intelligence-suite": {
    eyebrow: "Automatización documental",
    headline: "Convierte PDFs, facturas, recibos y CVs en datos estructurados.",
    subheadline: "Document Intelligence Suite agrupa OCR, parsing, extracción y export para backoffice, contabilidad, recruiting y legal.",
    promise: "Menos digitación manual, más datos reutilizables y outputs listos para operación.",
    audience: "Operaciones, contabilidad, backoffice, recruiters, consultoras y equipos legales.",
    ctaLabel: "Procesar un documento",
    secondaryCtaLabel: "Ver módulos",
    painPoints: [
      "Los documentos llegan en formatos difíciles de reutilizar.",
      "La digitación manual genera errores y retrabajo.",
      "Las tablas y campos quedan atrapados en PDFs.",
      "No hay confidence ni trazabilidad por extracción.",
    ],
    outcomes: [
      "JSON/CSV/XLSX desde PDFs, recibos, facturas y CVs.",
      "OCR y parsing modular según documento.",
      "Flags de validación y revisión humana para casos sensibles.",
      "Artifacts descargables y auditoría de runs.",
    ],
    proofPoints: ["Workers OCR/PDF", "Artifacts", "Run tokens", "Rate/fraud guard"],
    demoScenario: "Sube o describe una factura/recibo y extrae proveedor, fecha, total, items y alertas.",
    trafficAngle: "Pauta B2B: deja de copiar datos de PDFs a mano.",
    faqs: [
      { question: "Procesa PDFs escaneados?", answer: "Sí si el OCR/provider está configurado y la calidad del archivo lo permite." },
      { question: "Entrega Excel?", answer: "El producto está diseñado para JSON, CSV/XLSX y artifacts descargables." },
      { question: "Es 100% exacto?", answer: "No. Entrega confidence/flags y debe revisarse cuando el documento sea crítico." },
    ],
  },
  "web:commerce-content-studio": {
    eyebrow: "Contenido que vende",
    headline: "Crea descripciones, SEO, emails, QR y cálculos comerciales para vender más rápido.",
    subheadline: "Commerce Content Studio empaqueta copy, meta tags, plantillas y assets de ecommerce en un flujo pagable.",
    promise: "Lanza productos y campañas en minutos sin inventar cada pieza desde cero.",
    audience: "Ecommerce, marketplaces, agencias, microcomercios y equipos de marketing.",
    ctaLabel: "Crear contenido comercial",
    secondaryCtaLabel: "Ver outputs",
    painPoints: [
      "Publicar productos toma demasiado tiempo.",
      "El SEO y los bullets quedan incompletos.",
      "Las campañas no tienen HTML/export listo.",
      "Los márgenes se calculan aparte y con errores.",
    ],
    outcomes: [
      "Descripciones y bullets por canal.",
      "Meta tags, OG/Twitter y warnings.",
      "HTML/MJML para email marketing.",
      "QR/vCard y cálculos de margen/fees.",
    ],
    proofPoints: ["Workers de copy", "HTML export", "Checkout por run", "Prompts auditables"],
    demoScenario: "Describe un producto y genera descripción marketplace, SEO title, email corto y QR de contacto.",
    trafficAngle: "Pauta a ecommerce: publica productos mejor y más rápido.",
    faqs: [
      { question: "Puede inventar claims?", answer: "No debe hacerlo. El usuario debe aportar atributos reales y revisar antes de publicar." },
      { question: "Sirve para MercadoLibre/Shopify?", answer: "Sí como generación de contenido y assets reutilizables por canal." },
      { question: "Hay plan gratis?", answer: "El producto soporta preview gratis y planes pagos según configuración." },
    ],
  },
  "web:text-intelligence-api": {
    eyebrow: "APIs de texto listas para integrar",
    headline: "Clasifica, extrae entidades, analiza sentimiento y ajusta tono desde un solo producto.",
    subheadline: "Text Intelligence API empaqueta workers de NLP para soporte, CRM, operaciones y automatizaciones internas.",
    promise: "Convierte texto suelto en señales, etiquetas y respuestas accionables.",
    audience: "SaaS, equipos de soporte, CRMs, operaciones y builders que necesitan texto estructurado.",
    ctaLabel: "Probar análisis de texto",
    secondaryCtaLabel: "Ver módulos API",
    painPoints: [
      "Los mensajes de clientes no se clasifican automáticamente.",
      "Las entidades importantes quedan escondidas.",
      "El tono de emails y respuestas varía demasiado.",
      "Cada mini-modelo vive separado y sin auditoría.",
    ],
    outcomes: [
      "Sentiment, clasificación y NER.",
      "Regex y validaciones explicadas.",
      "Ajuste de tono y reescritura responsable.",
      "Outputs JSON listos para integrar.",
    ],
    proofPoints: ["JSON output", "Rate limits", "XAI/Ollama-ready", "Audit logs"],
    demoScenario: "Pega 10 mensajes de clientes y clasifica intención, sentimiento, entidades y respuesta sugerida.",
    trafficAngle: "Pauta B2B técnica: agrega inteligencia de texto sin construir cinco APIs separadas.",
    faqs: [
      { question: "Es API o interfaz?", answer: "Ambas: tiene interfaz de producto y puede exponerse como API con workspace/uso." },
      { question: "Guarda PII?", answer: "Debe minimizarse y tratarse según la política de retención del workspace." },
      { question: "Puede usarse para spam?", answer: "No. Tiene guardrails contra abuso y usos no permitidos." },
    ],
  },
  "web:legal-compliance-desk": {
    eyebrow: "Documentos legales revisables",
    headline: "Resume contratos, detecta riesgos y prepara borradores legales sin vender humo.",
    subheadline: "Legal & Compliance Desk ayuda a ordenar documentos, red flags, derechos de petición y multas con disclaimers fuertes.",
    promise: "Mejores borradores y más claridad antes de consultar, negociar o enviar.",
    audience: "Personas, pymes, freelancers y equipos que necesitan orientación documental.",
    ctaLabel: "Revisar un documento",
    secondaryCtaLabel: "Ver límites responsables",
    painPoints: [
      "Los contratos se firman sin entender riesgos.",
      "Los reclamos se redactan sin estructura ni evidencia.",
      "Los plazos y obligaciones se pasan por alto.",
      "Los usuarios confunden IA con asesoría definitiva.",
    ],
    outcomes: [
      "Resumen claro, obligaciones y red flags.",
      "Borradores de petición, PQRS o multa.",
      "Checklist de evidencia y revisión.",
      "Disclaimers y human review por defecto.",
    ],
    proofPoints: ["Human review", "Auditabilidad", "Disclaimers", "PDF/doc ready"],
    demoScenario: "Pega una cláusula o describe una multa y recibe riesgos, preguntas y un borrador revisable.",
    trafficAngle: "Pauta legal responsable: prepara mejor tu caso antes de actuar.",
    faqs: [
      { question: "Es abogado?", answer: "No. Es orientación informativa y generación de borradores. No reemplaza asesoría legal definitiva." },
      { question: "Puedo enviar el documento tal cual?", answer: "No recomendamos enviar documentos sensibles sin revisión humana/profesional." },
      { question: "Sirve para contratos?", answer: "Sí para resumen, obligaciones, riesgos y preguntas de revisión." },
    ],
  },
  "web:business-docs-finance-studio": {
    eyebrow: "Documentos comerciales y finanzas ligeras",
    headline: "Genera propuestas, cotizaciones, auditorías y comparativos para cerrar mejor.",
    subheadline: "Business Docs & Finance Studio convierte pedidos informales en documentos comerciales con supuestos y trazabilidad.",
    promise: "Más velocidad comercial sin perder claridad financiera.",
    audience: "Consultores, agencias, pymes y equipos comerciales.",
    ctaLabel: "Crear documento comercial",
    secondaryCtaLabel: "Ver módulos",
    painPoints: [
      "Cada propuesta empieza desde cero.",
      "Las cotizaciones no explican alcance ni condiciones.",
      "Los comparativos de proveedores quedan subjetivos.",
      "Los supuestos financieros no se documentan.",
    ],
    outcomes: [
      "Propuestas y cotizaciones PDF-ready.",
      "Auditorías y comparativos ejecutivos.",
      "Inventario y alertas operativas.",
      "Supuestos visibles antes de compartir.",
    ],
    proofPoints: ["RunToken", "PDF-ready", "Supuestos", "Workspace comercial"],
    demoScenario: "Describe un proyecto y genera una propuesta con alcance, precio, timeline y condiciones.",
    trafficAngle: "Pauta a consultores/pymes: convierte mensajes de cliente en documentos que venden.",
    faqs: [
      { question: "Hace contabilidad oficial?", answer: "No. Genera documentos y análisis operativos, no certificación contable." },
      { question: "Puedo exportar?", answer: "El backend está preparado para outputs y artifacts según módulo." },
      { question: "Sirve para agencias?", answer: "Sí, especialmente para propuestas, cotizaciones y reportes rápidos." },
    ],
  },
  "web:marketing-sales-intelligence": {
    eyebrow: "Inteligencia comercial para growth",
    headline: "Audita landings, valida emails, detecta stack web y prepara señales de venta.",
    subheadline: "Marketing & Sales Intelligence junta herramientas de growth con APIs oficiales, rate limits y auditoría.",
    promise: "Mejores listas, mejores diagnósticos y menos prospección a ciegas.",
    audience: "Agencias, growth teams, ventas B2B y founders.",
    ctaLabel: "Analizar oportunidad",
    secondaryCtaLabel: "Ver señales",
    painPoints: [
      "Las landings no convierten y nadie sabe por qué.",
      "Los emails rebotan antes de vender.",
      "El stack técnico del prospecto se investiga manualmente.",
      "Las fuentes externas pueden romper compliance.",
    ],
    outcomes: [
      "Crítica de landing y copy.",
      "Validación de emails y URL safety.",
      "Detección de tecnología web.",
      "Prospección con APIs oficiales cuando aplica.",
    ],
    proofPoints: ["Rate limits", "APIs oficiales", "Fraud guard", "Auditoría"],
    demoScenario: "Pega una landing y un dominio prospecto para recibir diagnóstico, stack y siguientes acciones.",
    trafficAngle: "Pauta a agencias/growth: encuentra oportunidades de mejora sin investigación manual larga.",
    faqs: [
      { question: "Hace scraping agresivo?", answer: "No. Los módulos sensibles deben usar APIs oficiales o datos aportados por el usuario." },
      { question: "Valida todos los emails?", answer: "Puede validar sintaxis/MX/disposable y score; no garantiza entregabilidad perfecta." },
      { question: "Sirve para ventas B2B?", answer: "Sí, como apoyo de investigación y priorización." },
    ],
  },
};

export const productLandingVariants: Record<string, LandingVariant[]> = {
  "messaging:tiendita-bot-os": [
    {
      id: "control",
      name: "Control",
      rationale: "Promesa balanceada: tienda, pedidos e inventario.",
    },
    {
      id: "pain",
      name: "Dolor operativo",
      rationale: "Ataca pedidos perdidos y caos de chat.",
      headline: "Deja de perder pedidos por vender todo desde chats desordenados.",
      subheadline: "TienditaBot OS convierte conversaciones de WhatsApp en pedidos, stock, recibos y seguimiento sin obligarte a montar un ecommerce complejo.",
      promise: "Cada pedido queda más claro: producto, precio, cliente, pago, entrega y próximo paso.",
      ctaLabel: "Ordenar mis ventas",
    },
    {
      id: "roi",
      name: "Revenue",
      rationale: "Promesa de más ventas y recompra.",
      headline: "Vende por WhatsApp con catálogo, pagos y fidelidad en vez de responder todo a mano.",
      promise: "Más velocidad para cerrar pedidos, menos errores de stock y más recompra con tarjetas de fidelidad.",
      ctaLabel: "Activar ventas por WhatsApp",
    },
  ],
  "messaging:arriendo-bot-propertyops": [
    {
      id: "control",
      name: "Control",
      rationale: "Promesa balanceada: pagos, mora y mantenimiento.",
    },
    {
      id: "pain",
      name: "Mora",
      rationale: "Enfocado en el dolor más frecuente: cobros y mora.",
      headline: "No vuelvas a perseguir arriendos, recibos y mora desde memoria.",
      subheadline: "ArriendoBot centraliza inquilinos, vencimientos, recordatorios y mantenimiento para que cada propiedad tenga historial claro.",
      promise: "Cada cobro, mora y reporte queda trazado por propiedad e inquilino.",
      ctaLabel: "Controlar mis arriendos",
    },
    {
      id: "roi",
      name: "Administrador",
      rationale: "Enfocado en profesionalizar operación y ahorrar tiempo.",
      headline: "Un mini sistema de administración para propietarios que todavía usan solo WhatsApp.",
      promise: "Menos seguimiento manual, comunicaciones más serias y mejor historial para tomar decisiones.",
      ctaLabel: "Crear mi workspace de propiedades",
    },
  ],
  "messaging:agendabot-personal-os": [
    {
      id: "control",
      name: "Control",
      rationale: "Promesa balanceada: voz, tareas y agenda.",
    },
    {
      id: "pain",
      name: "Olvidos",
      rationale: "Ataca el dolor emocional de olvidar pendientes.",
      headline: "Convierte audios y mensajes sueltos en una agenda que sí te recuerda.",
      subheadline: "AgendaBot extrae tareas, fechas, recurrencias y prioridades desde texto o voz para que los pendientes no mueran en el chat.",
      promise: "Menos cosas olvidadas y más claridad diaria sin abrir otra app compleja.",
      ctaLabel: "Crear mi asistente de agenda",
    },
    {
      id: "roi",
      name: "Productividad",
      rationale: "Enfocado en daily planning y profesionales ocupados.",
      headline: "Tu asistente personal para capturar tareas, citas y proyectos desde WhatsApp.",
      promise: "Captura rápido, confirma antes de actuar y recibe digest diario de lo importante.",
      ctaLabel: "Probar agenda por voz",
    },
  ],
};

export const productLandingProofs: Record<string, LandingProof[]> = {
  "messaging:tiendita-bot-os": [
    {
      title: "Demo screenshot: pedido estructurado",
      metric: "4 datos críticos capturados",
      caption: "Producto, talla, precio y entrega salen del chat y quedan listos para confirmar.",
      sampleInput: "Cliente: quiero la camiseta negra talla M, pago por Nequi y envío a Laureles.",
      sampleOutput: "Pedido #47 · Camiseta negra M · $53.000 con envío · Estado: pendiente de pago.",
    },
    {
      title: "Demo screenshot: alerta de stock",
      metric: "2 productos por reponer",
      caption: "El vendedor ve qué se está agotando antes de prometer disponibilidad.",
      sampleInput: "Vendí 3 camisetas negras M y 2 pantalones azules 32.",
      sampleOutput: "Stock actualizado · Camiseta negra M: quedan 2 · Pantalón azul 32: agotado.",
    },
    {
      title: "Demo screenshot: fidelidad",
      metric: "8/10 compras",
      caption: "La tarjeta de sellos digital mantiene recompra sin papel.",
      sampleInput: "Café #8 de Carlos.",
      sampleOutput: "Carlos va 8/10. Le faltan 2 compras para premio automático.",
    },
  ],
  "messaging:arriendo-bot-propertyops": [
    {
      title: "Demo screenshot: mora detectada",
      metric: "15 días de mora",
      caption: "El propietario ve el estado y puede preparar comunicación formal.",
      sampleInput: "Juan del 302 no pagó este mes.",
      sampleOutput: "Apt 302 · Mora 15 días · último recordatorio hace 7 días · sugerir segundo aviso.",
    },
    {
      title: "Demo screenshot: mantenimiento",
      metric: "1 caso abierto",
      caption: "Daño, técnico, presupuesto y estado quedan en historial.",
      sampleInput: "Se rompió el calentador del 401.",
      sampleOutput: "Reporte creado · Calentador 401 · técnicos sugeridos · presupuesto mensual disponible.",
    },
    {
      title: "Demo screenshot: recibo",
      metric: "Recibo listo",
      caption: "El recibo queda preparado con datos básicos y trazabilidad.",
      sampleInput: "Pago recibido de María, apto 201, $1.800.000.",
      sampleOutput: "Recibo generado · Abril 2026 · Inquilina María · Estado: pagado.",
    },
  ],
  "messaging:agendabot-personal-os": [
    {
      title: "Demo screenshot: voz a tarea",
      metric: "3 campos extraídos",
      caption: "Título, fecha y prioridad salen de una nota de voz o texto.",
      sampleInput: "Recuérdame llamar al dentista mañana a las 10.",
      sampleOutput: "Recordatorio · Llamar al dentista · mañana 10:00 · prioridad normal.",
    },
    {
      title: "Demo screenshot: evento recurrente",
      metric: "Recurrencia detectada",
      caption: "AgendaBot entiende periodicidad y pide confirmación antes de invitar.",
      sampleInput: "Reunión con Juan todos los martes a las 3.",
      sampleOutput: "Evento semanal · Juan · martes 3:00 PM · ¿enviar invitación?",
    },
    {
      title: "Demo screenshot: digest diario",
      metric: "5 pendientes resumidos",
      caption: "El usuario recibe lo vencido, lo importante y el plan del día.",
      sampleInput: "¿Qué tengo hoy?",
      sampleOutput: "Hoy: 9am standup, 2pm proveedor, 4:30 gym · 2 pendientes vencidos.",
    },
  ],
};

export const genericLandingProofs: LandingProof[] = [
  {
    title: "Demo screenshot: input a output",
    metric: "1 flujo completo",
    caption: "La landing conecta el dolor del anuncio con una ejecución real del producto.",
    sampleInput: "Usuario describe el caso, documento, mensaje o tarea que quiere resolver.",
    sampleOutput: "El backend valida pago/workspace, ejecuta workers y registra auditoría.",
  },
  {
    title: "Demo screenshot: control de pago",
    metric: "Pago antes del run",
    caption: "Los módulos pagos pasan por checkout, runToken o suscripción activa.",
    sampleInput: "Click en CTA de producto.",
    sampleOutput: "Checkout/workspace creado · ejecución bloqueada si no hay pago válido.",
  },
  {
    title: "Demo screenshot: límites responsables",
    metric: "Guardrails activos",
    caption: "Los módulos sensibles muestran disclaimers y revisión humana.",
    sampleInput: "Caso legal, médico, broadcast o decisión sensible.",
    sampleOutput: "Orientación/borrador · no asesoría definitiva · revisión humana requerida.",
  },
];

export function landingKey(kind: "web" | "messaging", productId: string) {
  return `${kind}:${productId}`;
}
